import { faCaretDown, faCaretUp, faKeyboard, faXmark } from "@fortawesome/free-solid-svg-icons";
import If from "components/common/If";
import React, { createRef, useContext } from "react"
import i18n from 'config/translations/i18n';
import { DataSourceRequestType } from "types/datalist-types";
import { DropdownInputPropsType, InputErrorType } from "types/form-types";
import { getTranslationText } from "utilities/translation-utilities"
import '../../Form.scss';
import InputErrors from "../input-common/InputErrors";
import InputExtension from "../input-common/InputExtension"
import InputLabel from "../input-common/InputLabel";
import DropdownItemsInput from "./DropdownItemsInput";
import { FormContextManager, getFormContext } from "config/FormContext";
import DropdownDialog, {  } from "./DropdownDialog";

const DATA_LIST_TAKE = 5;

type CoreDropdownInputPropsType<T, TFilterType, TSortType> = DropdownInputPropsType<T, TFilterType, TSortType> & {
    prefixes?: React.ReactNode,
    validate: (soft: boolean, input: HTMLInputElement | undefined, inputWrapper: HTMLDivElement | undefined) => InputErrorType[],
    formContext: FormContextManager
}

type CoreDropdownInputStateType<T> = {
    touched: boolean
    errors: InputErrorType[],
    inputValue: string,
    data: T[],
    opened: boolean,
    loading: boolean,
    search: boolean,
    isFocused: boolean
    dropdownStyle: {
        top: number,
        left: number,
        width: number,
        minHeight: number
    }
}

export class CoreDropdownInputClass<T, TFilterType, TSortType> extends React.Component<CoreDropdownInputPropsType<T, TFilterType, TSortType>, CoreDropdownInputStateType<T>> {
    private readonly t = i18n.t;
    private readonly input = createRef<HTMLInputElement>();
    private readonly inputWrapper = createRef<HTMLDivElement>();
    private readonly placeholder: string | null | undefined;
    constructor(props: CoreDropdownInputPropsType<T, TFilterType, TSortType>) {
        super(props)
        this.state = {
            touched: props.formContext.submitted || props.touched || props.value ? true : false,
            errors: [],
            data: [],
            inputValue: '',
            loading: false,
            opened: false,
            search: props.dataSource.search === 'switch' || props.dataSource.search === undefined ? false : true,
            isFocused: false,
            dropdownStyle: {
                top: 0,
                left: 0,
                width: 0,
                minHeight: 0,
            }
        }
        this.placeholder = getTranslationText(this.props.placeholder, this.t);
    }

    componentDidMount(): void {
        document.addEventListener('click', this.onFocusOut);
        document.addEventListener('keyup', this.onFocusOut);
        document.getElementsByTagName('main')[0].addEventListener('scroll', this.handleScroll);
        this.validate(!this.state.touched);
    }

    componentDidUpdate(prevProps: Readonly<CoreDropdownInputPropsType<T, TFilterType, TSortType>>, prevState: Readonly<CoreDropdownInputStateType<T>>, _snapshot?: any): void {
        if (prevProps.value != this.props.value) {
            this.setState({
                touched: true,
                inputValue: ''
            })
            this.validate(true);
        } else if (prevProps.value === this.props.value && prevState.inputValue !== this.state.inputValue && this.state.opened) {
            this.loadData().then();
        } else if (prevProps.formContext.submitted !== this.props.formContext.submitted) {
            this.setState({
                touched: true,
                inputValue: ''
            })
            this.validate(prevProps.formContext.submitted);
        }

        if (!prevState.opened && this.state.opened) {
            this.loadData().then();
        }
    }

    componentWillUnmount(): void {
        document.removeEventListener('click', this.onFocusOut);
        document.removeEventListener('keyup', this.onFocusOut);
        document.getElementsByTagName('main')[0].removeEventListener('scroll', this.handleScroll);
    }

    handleScroll = () => {
        if (this.state.opened) {
            const position = this.calculateDropdownPosition();
            this.setState({
                dropdownStyle: position
            });
        }
    };

    public getInputValue = (): string | undefined => {
        return this.input.current?.value;
    }

    private onInputValueChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (Array.isArray('data' in this.props.dataSource && this.props.dataSource.data)) return;
        this.setState({
            inputValue: event.target.value
        })
    }

    private onInputFocus = async (event: React.FocusEvent<HTMLInputElement>) => {
        if (this.props.disabled || this.props.readonly) {
            this.setState({
                opened: false,
                isFocused: true
            });
            return;
        }

        event.target.select();
        await this.loadData('');
        this.setOpened(true, { touched: true });
    }

    private onFocusOut = (e: MouseEvent | KeyboardEvent | FocusEvent) => {
        if (this.props.disabled || this.props.readonly) {
            this.setState({
                opened: false
            });
            return;
        }
        if (this.inputWrapper.current && this.inputWrapper.current.contains(e.target as Node)) {
            return;
        }

        this.setOpened(false, {
            inputValue: ''
        });
        this.validate(!this.state.touched);
    }

    private setOpened = (open: boolean, state?: Partial<CoreDropdownInputStateType<T>>) => {
        if (this.props.disabled || this.props.readonly) {
            this.setState({
                opened: false
            });
            return;
        }

        this.setState({
            ...this.state,
            opened: open,
            ...state,
            dropdownStyle: this.calculateDropdownPosition(open)
        });

        if (!open && this.state.touched) this.validate(this.state.touched);
    }

    private calculateDropdownPosition = (open?: boolean) => {
        let dropdownStyle = {
            top: 0,
            left: 0,
            width: 0,
            minHeight: 0
        };

        open = open === undefined ? this.state.opened : open;

        if (open) {
            const rect = this.inputWrapper.current?.getBoundingClientRect();
            let top = (rect?.top || 0) + (window.scrollY || window.pageYOffset);
            let height = this.inputWrapper.current?.offsetHeight || 0;
            let left = (rect?.left || 0) + (window.scrollX || window.pageXOffset);
            let width = this.inputWrapper.current?.offsetWidth || 0;

            dropdownStyle = {
                top: top + height - 10,
                left: left,
                width: width,
                minHeight: height
            }
        }

        return dropdownStyle;
    }

    private onKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            this.setOpened(false);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            this.setOpened(true);
        }
    }

    private loadData = async (searchText?: string) => {
        this.setState({
            loading: true
        });
        const d = await resolveData<T, TFilterType, TSortType>(this.props.dataSource, {
            searchText: searchText === undefined || searchText === null ? this.state.inputValue : searchText,
            take: this.props.dataSource.take || DATA_LIST_TAKE
        });

        this.setState({
            loading: false,
            data: d
        });
    }

    // #region value

    private onClear = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        this.onChange(undefined, event);
    }

    private onChange = (i: T | undefined, event: React.MouseEvent | React.KeyboardEvent) => {
        if ((event as any).key === 'Tab' && event.type === 'keyup') return;

        if (typeof this.props.onChange == 'function') {
            this.props.onChange(i, event);
        }

        this.setOpened(false);
    };

    private validate = (soft?: boolean) => {
        if (this.state.touched) soft = false;

        const errors = this.props.validate(soft || false, this.input?.current || undefined, this.inputWrapper?.current || undefined);

        this.setState({
            errors
        })
    }

    private openKeyboard = () => {
        this.setState({
            search: !this.state.search
        })
    }
    // #endregion value

    private onBlue = (e: React.FocusEvent) => {
        this.setState({
            isFocused: false
        })
    }

    renderModal = () => {
        if (!this.state.opened) return <></>;

        // if (getScreenSize() == Screens.sm) {
        //     return <div
        //             className={`dropdown-wrapper ${this.state.opened ? 'active' : ''} overflow-y-auto max-h-52 dropdown-zindex`.trim()}
        //             style={getScreenSize() == Screens.sm ? { bottom: 0, left: 0, right: 0, top:0 } : { ...this.state.dropdownStyle }}
        //         >
        //         <div className="p-5">
        //             <header className="flex justify-between items-center border-dotted border-b theme-border ">
        //                 <h4>{this.props.label}</h4>
        //             </header>
        //             <div>
        //                 <DropdownItemsInput
        //                     {...this.props}
        //                     loading={this.state.loading}
        //                     data={this.state.data}
        //                     onItemClick={this.onChange}
        //                 />
        //             </div>
        //         </div>
        //     </div>
        // }

        return <div
            className={`dropdown-wrapper ${this.state.opened ? 'active' : ''} overflow-y-auto max-h-52 dropdown-zindex`.trim()}
            style={{ ...this.state.dropdownStyle }}
        >
            <DropdownItemsInput
                {...this.props}
                loading={this.state.loading}
                data={this.state.data}
                onItemClick={this.onChange}
            />
        </div>
    }

    // #region render
    render(): React.ReactNode {
        return (
            <>
                <div
                    ref={this.inputWrapper}
                    className={`app-input-wrapper dropdown-input relative box-border ${this.props.disabled ? 'disabled' : ''} ${this.props.readonly ? 'readonly' : ''}`.trim()}
                >
                    <label>
                        <InputLabel label={this.props.label} required={this.props.required} />
                        <div
                            className="input-container"
                        >
                            {this.props.prefixes}
                            <div className="flex">
                                <InputExtension icons={this.props.prefix} isFocused={this.state.isFocused} />
                                <input
                                    ref={this.input}
                                    type="text"
                                    name={this.props.name}
                                    autoFocus={this.props.autoFocus}
                                    size={50}
                                    className={`${this.props.prefix ? '' : 'pl'} ${this.props.suffix ? '' : 'pr'}`.trim()}
                                    onFocus={this.onInputFocus}
                                    inputMode={this.props.dataSource.search === 'off' || this.state.search === false ? 'none' : undefined}
                                    readOnly={this.props.readonly}
                                    disabled={this.props.disabled}
                                    onKeyUp={this.onKeyPress}
                                    value={this.state.inputValue || this.props.view.inputView(this.props.value) || ''}
                                    onChange={this.onInputValueChange}
                                    placeholder={`${this.placeholder || ''}${this.props.required ? ' *' : ''}`}
                                    onBlur={this.onBlue}
                                    onBlurCapture={this.onBlue}
                                />
                                <If condition={this.props.dataSource.search === 'switch' || this.props.dataSource.search === undefined}>
                                    <InputExtension isFocused={false} key='dropdown-keyboard' icons={{ icon: faKeyboard, name: 'open-keyboard', onClick: this.openKeyboard }} />
                                </If>
                                <InputExtension icons={this.props.suffix} isFocused={this.state.isFocused} />
                                <If condition={!this.props.required}>
                                    <InputExtension isFocused={false} key='dropdown-clear' icons={{ icon: faXmark, name: 'clear', hidden: () => this.props.value ? false : true, onClick: this.onClear }} />
                                </If>
                                <InputExtension isFocused={false} key='dropdown-open' icons={{ icon: faCaretDown, name: 'open', hidden: () => this.state.opened, onClick: (e) => this.setOpened(true) }} />
                                <InputExtension isFocused={false} key='dropdown-close' icons={{
                                    icon: faCaretUp, name: 'close', hidden: () => !this.state.opened, onClick: (e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        this.setOpened(false)
                                    }
                                }} />
                            </div>
                        </div>
                    </label>
                    {/* <div className={`dropdown-wrapper ${this.state.opened ? 'active' : ''} overflow-y-auto max-h-52 dropdown-zindex`.trim()}>
                        <DropdownItemsInput
                            {...this.props}
                            opened={this.state.opened}
                            loading={this.state.loading}
                            data={this.state.data}
                            onItemClick={this.onChange}
                        />
                    </div> */}
                    <InputErrors errors={this.state.errors} />
                </div>
                <DropdownDialog
                    isOpen={this.state.opened}
                >
                    {this.renderModal()}
                </DropdownDialog>
            </>
        );
    }
    // #endregion render
};

// #region private
const resolveData = async <T, TFilterType, TSortType>(dataSource: DropdownInputPropsType<T, TFilterType, TSortType>['dataSource'], options: DataSourceRequestType<TFilterType, TSortType>): Promise<T[]> => {
    if ('data' in dataSource && Array.isArray(dataSource.data)) {
        return [...dataSource.data]
    }

    if ('data' in dataSource && typeof dataSource.data === 'function') {
        return dataSource.data(options.searchText);
    }

    if ('request' in dataSource) {
        const data = await dataSource.request(options);
        return data;
    }

    return [];
}
// #endregion private

type CoreDropdownInputPropsWrapperType<T, TFilterType, TSortType> = Omit<CoreDropdownInputPropsType<T, TFilterType, TSortType>, 'formContext'> & {
    dropdownRef?: React.RefObject<CoreDropdownInputClass<T, TFilterType, TSortType> | null>
}

const CoreDropdownInput = <T, TFilterType, TSortType>(props: CoreDropdownInputPropsWrapperType<T, TFilterType, TSortType>) => {
    const formContext = getFormContext(useContext);
    return (
        <CoreDropdownInputClass
            {...props}
            ref={props.dropdownRef}
            value={props.value}
            formContext={formContext}
        />
    );
}

export default CoreDropdownInput;
